import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography, Spin, message, theme, Card, Table, Button, Tag,
  Row, Col, Modal, Form, Input, InputNumber, DatePicker, Select,
  Upload, List, Divider, Empty, Descriptions,
} from 'antd';
import PujaSpinner from '../../components/PujaSpinner';
import {
  PlusOutlined,
  UploadOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { movimientoService } from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

// ── Formulario de nuevo movimiento ──────────────────────────────────────────
const ModalNuevoMovimiento = ({ open, onClose, onSuccess, sexo, stockPropietarios }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [detalles, setDetalles] = useState([]);
  const esHembra = sexo === 'F';

  // Cuando se llena el total, pre-poblar distribución según %
  const handleTotalChange = (total) => {
    if (!total || !stockPropietarios.length) return;
    const nuevosDetalles = stockPropietarios.map((prop) => ({
      id_venta: prop.id_venta,
      es_productor: prop.es_productor || false,
      nom_usuario: prop.nom_usuario,
      porcentaje: prop.porcentaje_venta || 0,
      cantidad: esHembra
        ? Math.round((prop.porcentaje_venta / 100) * total)
        : Math.round((prop.porcentaje_venta / 100) * total),
    }));
    setDetalles(nuevosDetalles);
  };

  const handleCantidadDetalle = (idx, valor) => {
    setDetalles((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, cantidad: valor } : d))
    );
  };

  const handleGuardar = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const detallesPayload = detalles.map((d) => ({
        id_venta: d.id_venta,
        es_productor: d.es_productor,
        ...(esHembra ? { cantidad: d.cantidad } : { dosis: d.cantidad }),
        tipo: esHembra ? 'aspiracion' : 'coleccion',
      }));

      const payload = {
        ...values,
        fecha_aspiracion: values.fecha
          ? values.fecha.format('YYYY-MM-DD')
          : null,
        fecha_movimiento: values.fecha
          ? values.fecha.format('YYYY-MM-DD')
          : null,
        detalles: detallesPayload,
      };
      delete payload.fecha;
      delete payload.total; // lo enviamos como total_embriones o total_dosis según campo

      onSuccess(payload);
      form.resetFields();
      setDetalles([]);
    } catch (err) {
      if (err?.errorFields) return;
      message.error('Error al guardar movimiento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={esHembra ? 'Registrar Aspiración' : 'Registrar Colección de Semen'}
      open={open}
      onCancel={() => { form.resetFields(); setDetalles([]); onClose(); }}
      onOk={handleGuardar}
      confirmLoading={loading}
      okText="Guardar"
      cancelText="Cancelar"
      width={600}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        <Row gutter={12}>
          <Col xs={12}>
            <Form.Item name="fecha" label="Fecha" rules={[{ required: true }]}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              name={esHembra ? 'total_embriones' : 'total_dosis'}
              label={esHembra ? 'Total embriones' : 'Total dosis'}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                onChange={handleTotalChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="proveedor" label="Proveedor">
          <Input placeholder="Nombre del laboratorio / veterinaria" />
        </Form.Item>

        {esHembra && (
          <Form.Item name="donante_toro" label="Donante / Toro">
            <Input placeholder="Nombre del toro donante" />
          </Form.Item>
        )}

        {!esHembra && (
          <Form.Item name="precio_lista" label="Precio lista">
            <InputNumber prefix="$" style={{ width: '100%' }} min={0} />
          </Form.Item>
        )}

        <Form.Item name="observaciones" label="Observaciones">
          <Input.TextArea rows={2} />
        </Form.Item>

        {/* Distribución por propietario */}
        {detalles.length > 0 && (
          <>
            <Divider>Distribución por propietario</Divider>
            {detalles.map((d, idx) => (
              <Row key={idx} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                <Col flex="auto">
                  <Text>{d.nom_usuario} ({d.porcentaje}%)</Text>
                </Col>
                <Col>
                  <InputNumber
                    value={d.cantidad}
                    min={0}
                    onChange={(val) => handleCantidadDetalle(idx, val)}
                    style={{ width: 100 }}
                  />
                </Col>
              </Row>
            ))}
          </>
        )}
      </Form>
    </Modal>
  );
};

// ── Sección de documentos ────────────────────────────────────────────────────
const SeccionDocumentos = ({ tipo, idMovimiento }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargarDocs = useCallback(async () => {
    if (!idMovimiento) return;
    setLoading(true);
    try {
      const res = await movimientoService.getDocumentos(tipo, idMovimiento);
      setDocs(res.data || []);
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, [tipo, idMovimiento]);

  useEffect(() => { cargarDocs(); }, [cargarDocs]);

  const handleUpload = async ({ file }) => {
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('tipo_movimiento', tipo);
    formData.append('id_movimiento', idMovimiento);
    try {
      await movimientoService.subirDocumento(formData);
      message.success('Documento subido');
      cargarDocs();
    } catch {
      message.error('Error al subir documento');
    }
    return false; // evitar upload automático de antd
  };

  const getIcono = (tipoArchivo) => {
    if (!tipoArchivo) return <FileOutlined />;
    if (tipoArchivo.includes('pdf')) return <FilePdfOutlined />;
    if (tipoArchivo.includes('image')) return <FileImageOutlined />;
    return <FileOutlined />;
  };

  return (
    <div>
      <Upload customRequest={handleUpload} showUploadList={false}>
        <Button icon={<UploadOutlined />} disabled={!idMovimiento}>
          Adjuntar documento
        </Button>
      </Upload>

      {loading ? (
        <Spin size="small" style={{ marginTop: 12 }} />
      ) : docs.length === 0 ? (
        <Empty description="Sin documentos" style={{ padding: 16 }} />
      ) : (
        <List
          style={{ marginTop: 12 }}
          size="small"
          dataSource={docs}
          renderItem={(doc) => (
            <List.Item
              actions={[
                <a href={doc.url_archivo} target="_blank" rel="noreferrer">
                  Ver
                </a>,
              ]}
            >
              <List.Item.Meta
                avatar={getIcono(doc.tipo_archivo)}
                title={doc.nombre_archivo}
                description={doc.created_at?.slice(0, 10)}
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );
};

// ── Página principal ─────────────────────────────────────────────────────────
const MovimientoDetalle = () => {
  const { id_animal } = useParams();
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [movimientoSeleccionado, setMovimientoSeleccionado] = useState(null);

  const esHembra = datos?.animal?.sexo === 'F';

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const fn = esHembra !== false
        ? movimientoService.getHembra
        : movimientoService.getMacho;
      // Intentar hembra primero, si no tiene datos probar macho
      let res;
      try {
        res = await movimientoService.getHembra(id_animal);
        // Si el animal es macho, la ruta hembra igual devuelve datos del animal
      } catch {
        res = await movimientoService.getMacho(id_animal);
      }
      setDatos(res.data);
    } catch {
      message.error('Error al cargar movimientos');
    } finally {
      setLoading(false);
    }
  }, [id_animal]);

  useEffect(() => {
    // Determinar sexo primero, luego cargar la ruta correcta
    const cargarPorSexo = async () => {
      setLoading(true);
      try {
        // Intentar hembra — si el animal es macho, el backend igual devuelve el animal
        const resH = await movimientoService.getHembra(id_animal);
        const animal = resH.data?.animal;
        if (animal?.sexo === 'M') {
          const resM = await movimientoService.getMacho(id_animal);
          setDatos(resM.data);
        } else {
          setDatos(resH.data);
        }
      } catch {
        // Probar macho
        try {
          const resM = await movimientoService.getMacho(id_animal);
          setDatos(resM.data);
        } catch {
          message.error('Error al cargar movimientos');
        }
      } finally {
        setLoading(false);
      }
    };
    cargarPorSexo();
  }, [id_animal]);

  const stockPropietarios = useMemo(() => {
    if (!datos?.stock_propietarios) return [];
    return datos.stock_propietarios.map((sp) => ({
      ...sp,
      porcentaje_venta: sp.porcentaje_venta || 0,
    }));
  }, [datos]);

  // Columnas dinámicas por propietario
  const columnas = useMemo(() => {
    if (!datos) return [];

    const baseCols = esHembra
      ? [
          { title: 'Fecha', dataIndex: 'fecha_aspiracion', key: 'fecha', render: (v) => v || '-' },
          { title: 'Proveedor', dataIndex: 'proveedor', key: 'proveedor', render: (v) => v || '-' },
          { title: 'Donante/Toro', dataIndex: 'donante_toro', key: 'donante_toro', render: (v) => v || '-' },
          { title: 'Total', dataIndex: 'total_embriones', key: 'total', render: (v) => v ?? '-' },
        ]
      : [
          { title: 'Fecha', dataIndex: 'fecha_movimiento', key: 'fecha', render: (v) => v || '-' },
          { title: 'Proveedor', dataIndex: 'proveedor', key: 'proveedor', render: (v) => v || '-' },
          { title: 'Precio Lista', dataIndex: 'precio_lista', key: 'precio_lista', render: (v) => v ? `$${v}` : '-' },
          { title: 'Total Dosis', dataIndex: 'total_dosis', key: 'total', render: (v) => v ?? '-' },
        ];

    // Columna por propietario
    const propCols = stockPropietarios.map((prop) => ({
      title: `${prop.nom_usuario} (${prop.porcentaje_venta || 0}%)`,
      key: `prop_${prop.id_venta || 'productor'}`,
      render: (_, row) => {
        const det = row.detalles?.find((d) =>
          prop.es_productor ? d.es_productor : d.id_venta === prop.id_venta
        );
        if (!det) return '-';
        const val = esHembra ? det.cantidad : det.dosis;
        return (
          <Tag color={det.tipo === 'aspiracion' || det.tipo === 'coleccion' ? 'green' : 'red'}>
            {det.tipo === 'aspiracion' || det.tipo === 'coleccion' ? '+' : '-'}{val}
          </Tag>
        );
      },
    }));

    const obsCols = [
      { title: 'Observaciones', dataIndex: 'observaciones', key: 'obs', render: (v) => v || '-' },
      {
        title: 'Documentos',
        key: 'docs',
        render: (_, row) => (
          <Button
            size="small"
            onClick={() => setMovimientoSeleccionado(row.id_movimiento)}
            type={movimientoSeleccionado === row.id_movimiento ? 'primary' : 'default'}
          >
            Docs
          </Button>
        ),
      },
    ];

    return [...baseCols, ...propCols, ...obsCols];
  }, [datos, stockPropietarios, esHembra, movimientoSeleccionado]);

  // Fila de totales
  const filasTotales = useMemo(() => {
    if (!datos?.movimientos?.length) return [];
    const totals = {};
    stockPropietarios.forEach((prop) => {
      const clave = `prop_${prop.id_venta || 'productor'}`;
      totals[clave] = 0;
      datos.movimientos.forEach((mov) => {
        const det = mov.detalles?.find((d) =>
          prop.es_productor ? d.es_productor : d.id_venta === prop.id_venta
        );
        if (det) {
          const val = esHembra ? det.cantidad : det.dosis;
          const isPositive = det.tipo === 'aspiracion' || det.tipo === 'coleccion';
          totals[clave] += isPositive ? val : -val;
        }
      });
    });

    return [{
      key: '__totals__',
      _isTotals: true,
      ...totals,
    }];
  }, [datos, stockPropietarios, esHembra]);

  const handleNuevoMovimiento = async (payload) => {
    try {
      payload.id_animal = parseInt(id_animal);
      if (esHembra) {
        await movimientoService.crearHembra(payload);
      } else {
        await movimientoService.crearMacho(payload);
      }
      message.success('Movimiento registrado');
      setModalOpen(false);
      // Recargar
      const res = esHembra
        ? await movimientoService.getHembra(id_animal)
        : await movimientoService.getMacho(id_animal);
      setDatos(res.data);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Error al guardar';
      message.error(msg);
    }
  };

  if (loading) {
    return (
      <PujaSpinner size="lg" />
    );
  }

  if (!datos) {
    return <Empty description="Animal no encontrado" />;
  }

  const { animal, movimientos = [] } = datos;

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Row align="middle" gutter={16} style={{ marginBottom: 24 }}>
        <Col>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/ventas/movimientos')}
          >
            Volver
          </Button>
        </Col>
        <Col flex="auto">
          <Title level={3} style={{ margin: 0 }}>
            {animal?.nom_animal}
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
          >
            {esHembra ? 'Registrar Aspiración' : 'Registrar Colección'}
          </Button>
        </Col>
      </Row>

      {/* Info del animal */}
      <Card style={{ marginBottom: 24, borderRadius: 8 }}>
        <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="Nombre">{animal?.nom_animal}</Descriptions.Item>
          <Descriptions.Item label="Raza">{animal?.nom_raza || '-'}</Descriptions.Item>
          <Descriptions.Item label="Sexo">
            <Tag color={animal?.sexo === 'M' ? 'blue' : 'pink'}>
              {animal?.sexo === 'M' ? 'Macho' : 'Hembra'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="RP">{animal?.rp_animal || '-'}</Descriptions.Item>
          <Descriptions.Item label="Fecha Nac">{animal?.fecha_nac || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Stock actual */}
      {stockPropietarios.length > 0 && (
        <Card
          title="Stock actual por propietario"
          style={{ marginBottom: 24, borderRadius: 8 }}
        >
          <Row gutter={16}>
            {stockPropietarios.map((prop) => (
              <Col key={prop.id_venta || 'productor'} xs={12} sm={8} md={6}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {prop.nom_usuario}
                  </Text>
                  <div
                    style={{
                      fontSize: 28,
                      fontWeight: 700,
                      color: prop.stock >= 0 ? token.colorSuccess : token.colorError,
                    }}
                  >
                    {prop.stock}
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {esHembra ? 'embriones' : 'dosis'}
                  </Text>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Tabla de movimientos */}
      <Card
        title={esHembra ? 'Historial de aspiraciones' : 'Historial de colecciones'}
        style={{ marginBottom: 24, borderRadius: 8 }}
      >
        {movimientos.length === 0 ? (
          <Empty description="Sin movimientos registrados" />
        ) : (
          <Table
            dataSource={[...movimientos, ...filasTotales]}
            columns={columnas}
            rowKey={(r) => r.id_movimiento || r.key}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            rowClassName={(r) => r._isTotals ? 'row-totals' : ''}
          />
        )}
      </Card>

      {/* Documentos del movimiento seleccionado */}
      {movimientoSeleccionado && (
        <Card
          title={`Documentos — Movimiento #${movimientoSeleccionado}`}
          style={{ borderRadius: 8 }}
          extra={
            <Button size="small" onClick={() => setMovimientoSeleccionado(null)}>
              Cerrar
            </Button>
          }
        >
          <SeccionDocumentos
            tipo={esHembra ? 'hembra' : 'macho'}
            idMovimiento={movimientoSeleccionado}
          />
        </Card>
      )}

      {/* Modal nuevo movimiento */}
      <ModalNuevoMovimiento
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleNuevoMovimiento}
        sexo={animal?.sexo}
        stockPropietarios={stockPropietarios}
      />
    </div>
  );
};

export default MovimientoDetalle;
